import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box, Dialog, IconButton, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoTemplate, Tag } from 'types/auto-templates';
import { CollaboraFrame } from 'components/collabora-frame/collabora-frame';
import { templateFileId, useCollaboraSession } from 'services/auth';

import { AutocompleteVirtualize } from './autocomplete-virtualized';
import { ItemTag } from './item-tag';
import { ItemTagImageOption } from './item-tag-image-option';
import { ItemTagTextOption } from './item-tag-text';

type ModalEditTemplatesProps = {
  setOpenEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  openEditModal: boolean;
  template: AutoTemplate | undefined;
  tagsField: Tag[];
  tagsImages: Tag[];
  tagsSignatures: Tag[];
};

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    gap: theme.spacing(1.5),
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      padding: theme.spacing(1),
      gap: theme.spacing(1),
    },
  },
  picker: {
    flex: '1 1 0',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flex: '0 0 auto',
      width: '100%',
    },
  },
  closeButton: {
    flex: '0 0 auto',
    minWidth: theme.spacing(5.5),
    minHeight: theme.spacing(5.5),
    [theme.breakpoints.down('sm')]: {
      alignSelf: 'flex-end',
    },
  },
  frameRegion: {
    flex: '1 1 0',
    minHeight: 0,
    minWidth: 0,
  },
}));

export const ModalEditTemplate: FC<ModalEditTemplatesProps> = ({
  setOpenEditModal,
  openEditModal,
  template,
  tagsField,
  tagsImages,
  tagsSignatures,
}) => {
  const collabora = useCollaboraSession(
    template?.code ? templateFileId(template.code) : null,
    openEditModal,
    () => setOpenEditModal(false),
  );
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const classes = useStyles();

  const closeButton = (
    <IconButton
      className={classes.closeButton}
      aria-label={t('common:close')}
      onClick={() => setOpenEditModal(false)}
      size="large"
    >
      <FontAwesomeIcon icon="times" />
    </IconButton>
  );

  return (
    <Dialog
      open={openEditModal}
      onClose={() => setOpenEditModal(false)}
      fullScreen
    >
      <Box className={classes.toolbar}>
        {isPhone && closeButton}
        <Box className={classes.picker}>
          <AutocompleteVirtualize
            options={tagsField}
            label={t('documents:field-info')}
            size="small"
            renderOption={(tag) => (
              <ItemTag
                key={tag._id}
                tag={tag}
                options={() => { }}
              />
            )}
          />
        </Box>
        <Box className={classes.picker}>
          <AutocompleteVirtualize
            options={tagsImages}
            label={t('documents:images')}
            size="small"
            renderOption={(tag) => (
              <ItemTag
                key={tag._id}
                tag={tag}
                options={() => (
                  <>

                  </>
                )}
              />
            )}
          />
        </Box>
        <Box className={classes.picker}>
          <AutocompleteVirtualize
            options={tagsSignatures}
            label={t('documents:signatures')}
            size="small"
            renderOption={(tag) => (
              <ItemTag
                key={tag._id}
                tag={tag}
                options={() => (
                  <>
                  </>
                )}
              />
            )}
          />
        </Box>
        {!isPhone && closeButton}
      </Box>
      <Box className={classes.frameRegion}>
        <CollaboraFrame session={collabora} darkTheme={theme.palette.mode === 'dark'} />
      </Box>
    </Dialog>
  );
};
